import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { offersAPI } from '../utils/api';
import { FICQuoteBrief } from '../types';
import toast from 'react-hot-toast';
import {
  X,
  Loader2,
  Download,
  DownloadCloud,
  Check,
  FileText,
  Calendar,
  Euro,
  User,
} from 'lucide-react';

interface ImportFICQuotesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ImportFICQuotesModal({ isOpen, onClose }: ImportFICQuotesModalProps) {
  const queryClient = useQueryClient();
  const [importingId, setImportingId] = useState<number | null>(null);

  // Fetch FIC quotes
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['fic-quotes'],
    queryFn: async () => {
      const response = await offersAPI.getFICQuotes();
      return response.data;
    },
    enabled: isOpen,
  });

  // Import single quote mutation
  const importSingleMutation = useMutation({
    mutationFn: (ficId: number) => offersAPI.importFICQuote(ficId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offers'] });
      refetch();
      toast.success('Preventivo importato con successo');
      setImportingId(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nell\'importazione');
      setImportingId(null);
    },
  });

  // Import all quotes mutation
  const importAllMutation = useMutation({
    mutationFn: () => offersAPI.importAllFICQuotes(),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['offers'] });
      refetch();
      const { imported, skipped } = response.data;
      toast.success(`Importati ${imported} preventivi, ${skipped} già presenti`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nell\'importazione');
    },
  });

  const handleImportSingle = (ficId: number) => {
    setImportingId(ficId);
    importSingleMutation.mutate(ficId);
  };

  const handleImportAll = () => {
    importAllMutation.mutate();
  };

  const quotes = data?.data || [];
  const availableQuotes = quotes.filter(q => !q.already_imported);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT');
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const getStatusBadge = (stato?: string) => {
    const colors: Record<string, string> = {
      'not_sent': 'bg-gray-100 text-gray-700',
      'sent': 'bg-blue-100 text-blue-700',
      'accepted': 'bg-green-100 text-green-700',
      'rejected': 'bg-red-100 text-red-700',
      'expired': 'bg-yellow-100 text-yellow-700',
    };
    const labels: Record<string, string> = {
      'not_sent': 'Non inviato',
      'sent': 'Inviato',
      'accepted': 'Accettato',
      'rejected': 'Rifiutato',
      'expired': 'Scaduto',
    };
    const color = colors[stato || ''] || 'bg-gray-100 text-gray-700';
    const label = labels[stato || ''] || stato || 'N/A';
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${color}`}>
        {label}
      </span>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold">Importa Preventivi da Fatture in Cloud</h2>
            <p className="text-sm text-gray-500 mt-1">
              Seleziona i preventivi da importare come offerte
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600">Caricamento preventivi FIC...</span>
            </div>
          ) : quotes.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Nessun preventivo trovato in Fatture in Cloud</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Import All Button */}
              {availableQuotes.length > 0 && (
                <div className="flex justify-end mb-4">
                  <button
                    onClick={handleImportAll}
                    disabled={importAllMutation.isPending}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {importAllMutation.isPending ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <DownloadCloud className="h-5 w-5" />
                    )}
                    Importa tutti ({availableQuotes.length})
                  </button>
                </div>
              )}

              {/* Quotes Table */}
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Preventivo
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cliente
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Importo
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Stato FIC
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Azione
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {quotes.map((quote: FICQuoteBrief) => (
                    <tr
                      key={quote.fic_id}
                      className={quote.already_imported ? 'bg-gray-50' : 'hover:bg-gray-50'}
                    >
                      <td className="px-4 py-4">
                        <div className="flex items-center">
                          <FileText className="h-5 w-5 text-gray-400 mr-2" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {quote.numero || `#${quote.fic_id}`}
                            </div>
                            {quote.titolo && (
                              <div className="text-sm text-gray-500 truncate max-w-xs">
                                {quote.titolo}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center">
                          <User className="h-4 w-4 text-gray-400 mr-1" />
                          <span className="text-sm text-gray-900">
                            {quote.cliente_nome || 'N/A'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 text-gray-400 mr-1" />
                          <span className="text-sm text-gray-900">
                            {formatDate(quote.data)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <div className="flex items-center justify-end">
                          <Euro className="h-4 w-4 text-gray-400 mr-1" />
                          <span className="text-sm font-medium text-gray-900">
                            {formatAmount(quote.importo_totale)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-center">
                        {getStatusBadge(quote.stato)}
                      </td>
                      <td className="px-4 py-4 text-center">
                        {quote.already_imported ? (
                          <span className="inline-flex items-center gap-1 text-sm text-green-600">
                            <Check className="h-4 w-4" />
                            Importato
                          </span>
                        ) : (
                          <button
                            onClick={() => handleImportSingle(quote.fic_id)}
                            disabled={importingId === quote.fic_id || importAllMutation.isPending}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                          >
                            {importingId === quote.fic_id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Download className="h-4 w-4" />
                            )}
                            Importa
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Summary */}
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">
                    Totale preventivi: <strong>{quotes.length}</strong>
                  </span>
                  <span className="text-gray-600">
                    Già importati: <strong>{quotes.length - availableQuotes.length}</strong>
                  </span>
                  <span className="text-gray-600">
                    Da importare: <strong>{availableQuotes.length}</strong>
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
          >
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
}
